import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import config from 'config';
import { RootStateOrAny, useSelector } from 'react-redux';

import { getLineItems, getMembershipCartItem } from '../../utils/selectors';
import { useDrawerState } from '../CartDrawer/useDrawerState';

import { getProductTargetQuantity } from './helpers';
import type {
  ProductsInformation,
  ShoppingBagProduct,
  UpsellInformation,
} from './types';

type ProductsInformationInternal = {
  items: ShoppingBagProduct[];
  upsellInformation?: UpsellInformation;
  dateFirstAdded: string;
  productId: number;
  quantity: number;
  discounts: ShoppingBagProduct['discount'][];
};
type ProductMap = Map<string, ProductsInformationInternal>;

const bundleDiscountsPromoCodes = config.get(
  'public.promoCodes.bundleDiscounts'
);

type ProductFieldPriceGetter = (product: ShoppingBagProduct) => number;
const getPurchaseUnitPrice: ProductFieldPriceGetter = product =>
  product.purchaseUnitPrice;
const getDiscountAmount: ProductFieldPriceGetter = product =>
  product.discount?.amount ?? 0;
const sumUp = (
  products: ShoppingBagProduct[],
  getValue: ProductFieldPriceGetter
) => products.reduce((acc, curr) => acc + getValue(curr), 0);

export function getProductItemKey({ bundleItems, productId }) {
  if (!bundleItems || bundleItems?.length < 1) {
    return productId;
  }
  return `${productId}-${bundleItems
    .map(bundleItem => bundleItem.productId)
    .join('-')}`;
}

const sortByNewAdditionFirst = (
  a: ProductsInformation,
  b: ProductsInformation
) => (a.dateFirstAdded > b.dateFirstAdded ? -1 : 1);

const groupByFplAndSortByNewAddition = (
  a: ProductsInformation,
  b: ProductsInformation
) => {
  if (a.upsellInformation.fplId !== b.upsellInformation.fplId) {
    return a.upsellInformation.fplId - b.upsellInformation.fplId;
  } else {
    return sortByNewAdditionFirst(a, b);
  }
};

interface PreppedProductsWithEffects {
  products: [
    ProductsInformation[],
    ProductsInformation[],
    ProductsInformation[]
  ];
  effects: {
    hasExceedingStock: boolean;
  };
}

const pickDiscountsForProduct = (
  productDiscounts: ShoppingBagProduct['discount'][],
  product: ShoppingBagProduct
) => {
  const restDiscounts = [];
  const currentDiscounts = [];
  if (product.discount) {
    currentDiscounts.push(product.discount);
  }
  const productLineId = product.cartLineId || product.lineId;
  for (const discount of productDiscounts) {
    if (
      (discount?.cartLineId && discount.cartLineId === productLineId) ||
      (discount?.orderLineId && discount.orderLineId === product?.orderLineId)
    ) {
      currentDiscounts.push(discount);
    } else {
      restDiscounts.push(discount);
    }
  }
  return [restDiscounts, currentDiscounts];
};

export const toExternal = (
  v: ProductsInformationInternal
): ProductsInformation => {
  const firstInLine = v.items[0];
  const purchaseUnitPrice = sumUp(v.items, getPurchaseUnitPrice);
  const displayItemPrice =
    purchaseUnitPrice - sumUp(v.items, getDiscountAmount);

  return {
    items: v.items,
    upsellInformation: v.upsellInformation,
    dateFirstAdded: v.dateFirstAdded,
    discounts: v.discounts,
    product: {
      ...firstInLine,
      quantity: v.quantity,
      purchaseUnitPrice,
      displayItemPrice,
    },
  };
};

export const useShoppingBagProducts = () => {
  const productsWithMembership = useSelector(getLineItems);
  const membershipItem = useSelector(getMembershipCartItem);
  const membershipProductId = membershipItem?.productId;
  return useMemo(
    () =>
      !membershipProductId
        ? productsWithMembership
        : productsWithMembership.filter(
            ({ productId }) => productId !== membershipProductId
          ),
    [membershipProductId, productsWithMembership]
  );
};

function findLatestDate(products: ShoppingBagProduct[]) {
  if (products.length === 0) {
    return null;
  }
  if (products.length === 1) {
    return products[0];
  }
  const [{ dateAdded: firstDateAdded }, ...restProducts] = products;
  return restProducts.reduce((latestDate, { dateAdded }) => {
    if (dateAdded > latestDate) {
      return dateAdded;
    }
    return latestDate;
  }, firstDateAdded);
}

const useLatestProductOnDrawerOpened = (products: ShoppingBagProduct[]) => {
  const [timeLastOpened, setTimeLastOpened] = useState(null);
  const isOpeningTimed = timeLastOpened !== null;
  const [isDrawerOpen] = useDrawerState();
  useEffect(() => {
    if (isDrawerOpen && !isOpeningTimed) {
      // having timezone affecting calculation, simply write down
      // the last added product date to compare at the moment of
      // drawer opening
      setTimeLastOpened(findLatestDate(products));
    } else if (!isDrawerOpen && isOpeningTimed) {
      setTimeLastOpened(null);
    }
  }, [isDrawerOpen, isOpeningTimed, products, timeLastOpened]);
  return timeLastOpened;
};

const isItemQuantityExceedsStock = (
  product: ShoppingBagProduct,
  quantity: number
) => {
  const availableQuantity = getProductTargetQuantity(product);
  return availableQuantity < quantity;
};

export function usePrepWithProducts(
  products: ShoppingBagProduct[]
): PreppedProductsWithEffects {
  const latestDate = useLatestProductOnDrawerOpened(products);
  const { cartLineDiscounts: lineDiscounts } = useSelector(
    ({ checkout }: RootStateOrAny) => checkout
  );
  const featuredProductLocationIdDiscounts = useSelector(
    ({ checkout }: RootStateOrAny) =>
      checkout?.discounts?.reduce(
        (acc, discount) =>
          !discount?.featuredProductLocationId
            ? acc
            : {
                ...acc,
                [discount?.featuredProductLocationId]: discount,
              },
        {}
      )
  );

  const hasItemExceedingStock = useRef(false);

  const checkEligibilityForBundle = useCallback(
    fplIds => {
      for (let i = fplIds.length - 1; i >= 0; i--) {
        const fplId = fplIds[i];
        const cartHasDiscount = Object.hasOwn(
          featuredProductLocationIdDiscounts,
          fplId
        );
        const configurationHasDiscount = Object.hasOwn(
          bundleDiscountsPromoCodes,
          featuredProductLocationIdDiscounts[fplId]?.promoCode
        );
        if (cartHasDiscount && configurationHasDiscount) {
          const { promoCode, label, promoLabel } =
            featuredProductLocationIdDiscounts[fplId];

          const { quantity, url } = bundleDiscountsPromoCodes[promoCode];
          return {
            quantity,
            url,
            label: label || promoLabel,
            fplId,
          };
        }
      }
      return null;
    },
    [featuredProductLocationIdDiscounts]
  );

  const [newItems, qualifying, rest] = useMemo(() => {
    const m = new Map() as ProductMap;
    const upsellCounter: Record<number, number> = {};
    const discountHashtable: Record<number, Record<number, boolean>> = {};
    let productDiscounts = [...lineDiscounts];
    for (const product of products) {
      const { discount, fplIds, dateAdded } = product;
      let currentDiscounts = [];

      [productDiscounts, currentDiscounts] = pickDiscountsForProduct(
        productDiscounts,
        product
      );

      const key = getProductItemKey(product);
      if (!discountHashtable[key]) {
        discountHashtable[key] = {};
      }
      let entry = m.get(key);
      if (!entry) {
        entry = {
          items: [product],
          dateFirstAdded: dateAdded,
          productId: product.productId,
          discounts: [],
          quantity: 1,
        };
      } else {
        entry.items.push(product);
        entry.quantity++;
        if (entry.dateFirstAdded > dateAdded) {
          entry.dateFirstAdded = dateAdded;
        }
      }
      // handle discounts
      if (currentDiscounts.length > 0) {
        for (const discount of currentDiscounts) {
          if (!discountHashtable[key][discount?.promoCode]) {
            discountHashtable[key][discount?.promoCode] = true;
            entry.discounts.push(discount);
          }
        }
      }

      // TODO: verify this won't fire on non-bundle discounts
      const alreadyBundled =
        discount?.promoCode &&
        Object.hasOwn(bundleDiscountsPromoCodes, discount.promoCode);

      if (!alreadyBundled) {
        const upsellInformation = checkEligibilityForBundle(fplIds);
        if (upsellInformation?.fplId) {
          entry.upsellInformation = upsellInformation;
          if (Object.hasOwn(upsellCounter, upsellInformation.fplId)) {
            upsellCounter[upsellInformation.fplId]++;
          } else {
            upsellCounter[upsellInformation.fplId] = 1;
          }
        }
      }

      m.set(key, entry);
    }

    // @ts-ignore
    for (const [, { upsellInformation }] of m.entries()) {
      if (
        upsellInformation?.fplId &&
        Object.hasOwn(upsellCounter, upsellInformation.fplId)
      ) {
        upsellInformation.remainingItemsForBundle =
          upsellInformation.quantity - upsellCounter[upsellInformation.fplId];
      }
    }
    const newItems = [];
    const qualifying = [];
    const rest = [];

    hasItemExceedingStock.current = false;
    // @ts-ignore
    for (const [, v] of m.entries()) {
      const [product] = v.items;
      if (isItemQuantityExceedsStock(product, v.quantity)) {
        hasItemExceedingStock.current = true;
      }

      if (v?.upsellInformation?.remainingItemsForBundle > 0) {
        qualifying.push(toExternal(v));
      } else if (v.dateFirstAdded > latestDate) {
        newItems.push(toExternal(v));
      } else {
        rest.push(toExternal(v));
      }
    }

    return [newItems, qualifying, rest];
  }, [checkEligibilityForBundle, latestDate, lineDiscounts, products]);

  rest.sort(sortByNewAdditionFirst);
  qualifying.sort(groupByFplAndSortByNewAddition);

  return {
    products: [newItems, qualifying, rest],
    effects: {
      hasExceedingStock: hasItemExceedingStock.current,
    },
  };
}

export function usePrepProducts(): PreppedProductsWithEffects {
  const products = useShoppingBagProducts();
  return usePrepWithProducts(products);
}
